import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlusSquare, Edit3, Trash2, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import SEOSection from "@/components/admin/SEOSection";

const empty = {
  id: "",
  name: "",
  slug: "",
  description: "",
  parent_id: "",
  order: 0,
  active: true,
  show_in_menu: true,
  seo_title: "",
  seo_description: "",
  og_image: "",
  canonical_url: "",
};

export default function AdminCategoriesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "create", data: empty });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    api.adminListCategories()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const openCreate = () => setDialog({ open: true, mode: "create", data: { ...empty, order: (items.length + 1) * 10 } });
  const openEdit = (c) => setDialog({
    open: true, mode: "edit", data: {
      id: c.id, name: c.name, slug: c.slug, description: c.description || "",
      parent_id: c.parent_id || "", order: c.order || 0, active: c.active !== false, show_in_menu: c.show_in_menu !== false,
      seo_title: c.seo_title || "", seo_description: c.seo_description || "",
      og_image: c.og_image || "", canonical_url: c.canonical_url || "",
    }
  });

  const submit = async () => {
    const { data, mode } = dialog;
    if (!data.name.trim()) { toast.error("El nombre es obligatorio."); return; }
    setSaving(true);
    try {
      const payload = {
        name: data.name.trim(),
        slug: data.slug || null,
        description: data.description || null,
        parent_id: data.parent_id || null,
        order: parseInt(data.order || 0, 10),
        active: !!data.active,
        show_in_menu: !!data.show_in_menu,
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        og_image: data.og_image || null,
        canonical_url: data.canonical_url || null,
      };
      if (mode === "create") {
        await api.adminCreateCategory(payload);
        toast.success("Categoría creada.");
      } else {
        await api.adminUpdateCategory(data.id, payload);
        toast.success("Categoría actualizada.");
      }
      setDialog({ open: false, mode: "create", data: empty });
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) return;
    const doDelete = async (force = false) => {
      try {
        await api.adminDeleteCategory(confirmDelete.id, force);
        toast.success(force ? "Categoría eliminada (noticias movidas a papelera)" : "Categoría eliminada.");
        setConfirmDelete(null);
        reload();
      } catch (e) {
        const status = e?.response?.status;
        const d = e?.response?.data?.detail;
        if (!force && status === 409 && typeof d === "object" && d?.requires_force) {
          const msg = d.message || `Hay ${d.linked_count || "varias"} noticias asociadas.`;
          if (window.confirm(`${msg}\n\n¿Continuar y mover las noticias a la papelera?`)) {
            return doDelete(true);
          }
          setConfirmDelete(null);
          return;
        }
        toast.error(typeof d === "string" ? d : (d?.message || "No se pudo eliminar."));
      }
    };
    await doDelete(false);
  };

  const move = async (idx, dir) => {
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await api.adminReorderCategories(next.map((c) => c.id));
    } catch {
      toast.error("No se pudo actualizar el orden.");
      reload();
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.adminUpdateCategory(c.id, {
        name: c.name,
        slug: c.slug,
        description: c.description,
        parent_id: c.parent_id,
        order: c.order,
        active: !c.active,
        show_in_menu: c.show_in_menu,
      });
      reload();
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };

  return (
    <div data-testid={ATID.categoriesPage}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Estructura</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Categorías</h1>
        </div>
        <Button
          data-testid={ATID.categoryNew}
          onClick={openCreate}
          className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 px-4 uppercase text-xs tracking-widest font-semibold"
        >
          <PlusSquare className="w-4 h-4 mr-2" /> Nueva categoría
        </Button>
      </div>

      <div data-testid={ATID.categoriesTable} className="bg-white border border-softblue overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-3 w-24">Orden</th>
              <th className="text-left px-3 py-3">Nombre</th>
              <th className="text-left px-3 py-3">Slug</th>
              <th className="text-left px-3 py-3">Padre</th>
              <th className="text-left px-3 py-3">Noticias</th>
              <th className="text-center px-3 py-3">En menú</th>
              <th className="text-center px-3 py-3">Activa</th>
              <th className="text-right px-3 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-textgray">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-textgray">Todavía no hay categorías.</td></tr>
            )}
            {!loading && items.map((c, idx) => {
              const parent = items.find((x) => x.id === c.parent_id);
              return (
                <tr key={c.id} data-testid={ATID.categoryRow(c.id)} className="border-t border-softblue hover:bg-lightgray/40">
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="h-7 w-7 border border-softblue text-nightblue disabled:opacity-30 hover:bg-lightgray">
                        <ChevronUp className="w-3.5 h-3.5 mx-auto" />
                      </button>
                      <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-7 w-7 border border-softblue text-nightblue disabled:opacity-30 hover:bg-lightgray">
                        <ChevronDown className="w-3.5 h-3.5 mx-auto" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-blueblack">{c.name}</div>
                    {c.description && <div className="text-xs text-textgray line-clamp-1">{c.description}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-textgray">/{c.slug}</td>
                  <td className="px-3 py-3 text-textgray text-xs">{parent ? parent.name : "—"}</td>
                  <td className="px-3 py-3 text-textgray">{c.articles_count ?? 0}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${c.show_in_menu ? "bg-watergreen/20 text-nightblue" : "bg-lightgray text-textgray"}`}>
                      {c.show_in_menu ? "Sí" : "No"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <Switch
                      data-testid={ATID.categoryToggle(c.id)}
                      checked={!!c.active}
                      onCheckedChange={() => toggleActive(c)}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={ATID.categoryEdit(c.id)}
                        onClick={() => openEdit(c)}
                        className="inline-flex items-center justify-center h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray"
                        title="Editar"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={ATID.categoryDelete(c.id)}
                        onClick={() => setConfirmDelete(c)}
                        className="inline-flex items-center justify-center h-8 w-8 border border-softblue text-red-600 hover:bg-red-50"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid={ATID.categoryDialog} className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">{dialog.mode === "create" ? "Nueva categoría" : "Editar categoría"}</DialogTitle>
            <DialogDescription>Definí el nombre, slug y visibilidad de la sección.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre *</Label>
              <Input
                data-testid={ATID.categoryDialogName}
                value={dialog.data.name}
                onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, name: e.target.value } }))}
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Slug (opcional)</Label>
              <Input
                data-testid={ATID.categoryDialogSlug}
                value={dialog.data.slug}
                onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, slug: e.target.value } }))}
                placeholder="se genera desde el nombre si lo dejás vacío"
                className="mt-2 h-10 bg-lightgray border-softblue font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción</Label>
              <Textarea
                data-testid={ATID.categoryDialogDescription}
                value={dialog.data.description}
                onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, description: e.target.value } }))}
                rows={3}
                className="mt-2 bg-lightgray border-softblue"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Categoría padre</Label>
                <select
                  data-testid={ATID.categoryDialogParent}
                  value={dialog.data.parent_id}
                  onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, parent_id: e.target.value } }))}
                  className="mt-2 h-10 w-full bg-lightgray border border-softblue px-3 text-sm text-nightblue"
                >
                  <option value="">Ninguna (raíz)</option>
                  {items.filter((c) => c.id !== dialog.data.id).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Orden</Label>
                <Input
                  data-testid={ATID.categoryDialogOrder}
                  type="number"
                  value={dialog.data.order}
                  onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, order: e.target.value } }))}
                  className="mt-2 h-10 bg-lightgray border-softblue"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  data-testid={ATID.categoryDialogActive}
                  checked={dialog.data.active}
                  onCheckedChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, active: !!v } }))}
                />
                <span className="text-nightblue font-semibold">Activa</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <Switch
                  data-testid={ATID.categoryDialogMenu}
                  checked={dialog.data.show_in_menu}
                  onCheckedChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, show_in_menu: !!v } }))}
                />
                <span className="text-nightblue font-semibold">Mostrar en menú</span>
              </label>
            </div>
          </div>

          <SEOSection
            form={dialog.data}
            setField={(patch) => setDialog((d) => ({ ...d, data: { ...d.data, ...patch } }))}
            currentTitle={dialog.data.name}
            currentSummary={dialog.data.description}
            currentMainImage=""
            resourceType="category"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog((d) => ({ ...d, open: false }))} className="rounded-none">Cancelar</Button>
            <Button
              data-testid={ATID.categoryDialogSubmit}
              onClick={submit}
              disabled={saving}
              className="bg-nightblue hover:bg-blueblack text-white rounded-none uppercase text-xs tracking-widest font-semibold"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar la categoría <strong>{confirmDelete?.name}</strong>. Si tiene noticias asociadas, la operación será rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
